if (!customElements.get('product-card-swatches')) {
  class ProductCardSwatches extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.swatches = [];
      this.productCard = null;
    }

    connectedCallback() {
      this.productCard = this.closest('.product-card');
      this.swatches = this.querySelectorAll('.product-card__swatch');
      this.bindEvents();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    bindEvents() {
      this.listenersController = new AbortController();
      const { signal } = this.listenersController;
      this.swatches.forEach((swatch) => {
        swatch.addEventListener('click', this.handleSwatchClick.bind(this), { signal });
        swatch.addEventListener('keydown', this.handleSwatchKeydown.bind(this), { signal });
      });
    }

    cleanup() {
      if (this.listenersController) {
        this.listenersController.abort();
        this.listenersController = null;
      }
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    }

    get updateCardEnabled() {
      return this.dataset.updateCard === 'true';
    }

    handleSwatchKeydown(event) {
      if (event.key === 'Enter' || event.key === ' ') {
        if (this.updateCardEnabled) {
          event.preventDefault();
        }
        this.handleSwatchClick(event);
      }
    }

    async handleSwatchClick(event) {
      if (!this.updateCardEnabled) return;

      event.preventDefault();

      const swatch = event.currentTarget;

      if (swatch.classList.contains('active') || this.classList.contains('product-card-swatches--loading')) {
        return;
      }

      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();

      this.setLoadingState(swatch, true);
      this.setDisabledState(true);
      this.hideError();

      try {
        const html = await this.fetchCard(swatch.href);
        this.replaceCardContent(html);
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.showError();
        }
      } finally {
        this.setLoadingState(swatch, false);
        this.setDisabledState(false);
      }
    }

    async fetchCard(url) {
      const fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set('view', this.productCard?.dataset.cardView || 'card');

      const { signal } = this.abortController;
      const html = await theme.cache.request(fetchUrl.toString(), {
        init: { headers: { Accept: 'text/html' } },
      });

      signal.throwIfAborted();
      return html;
    }

    replaceCardContent(html) {
      if (!this.productCard) return;

      const quickAdd = this.productCard.querySelector('product-quick-add');
      const wasQuickAddOpen = quickAdd?.hasAttribute('open');

      const originalClasses = this.productCard.className;
      const originalStyle = this.productCard.getAttribute('style');
      const originalDataAttributes = {};

      Array.from(this.productCard.attributes).forEach((attr) => {
        if (attr.name.startsWith('data-')) {
          originalDataAttributes[attr.name] = attr.value;
        }
      });

      const doc = theme.utils.domParser.parseFromString(html, 'text/html');
      const newCard = doc.querySelector('.product-card');

      if (!newCard) return;

      this.productCard.innerHTML = newCard.innerHTML;

      this.productCard.className = originalClasses;

      if (originalStyle) {
        this.productCard.setAttribute('style', originalStyle);
      }

      Object.entries(originalDataAttributes).forEach(([name, value]) => {
        this.productCard.setAttribute(name, value);
      });

      const newSwatchesComponent = this.productCard.querySelector('product-card-swatches');
      if (newSwatchesComponent) {
        this.cleanup();
        this.swatches = newSwatchesComponent.querySelectorAll('.product-card__swatch');
        this.bindEvents();
      }

      if (wasQuickAddOpen) {
        const opener = this.productCard.querySelector('product-quick-add-opener');
        opener?.materialise?.().then((panel) => panel?.open()).catch(() => {});
      }

      const swatchUrl = this.productCard.querySelector('.product-card__info__title')?.href;
      if (swatchUrl) {
        document.dispatchEvent(
          new CustomEvent('product-swatch-changed', {
            detail: {
              productCard: this.productCard,
              productUrl: swatchUrl,
            },
          })
        );
      }
    }

    setLoadingState(swatch, isLoading) {
      if (isLoading) {
        swatch.classList.add('product-card__swatch--loading');
        swatch.setAttribute('aria-busy', 'true');
        this.classList.add('product-card-swatches--loading');
      } else {
        swatch.classList.remove('product-card__swatch--loading');
        swatch.removeAttribute('aria-busy');
        this.classList.remove('product-card-swatches--loading');
      }
    }

    setDisabledState(isDisabled) {
      this.swatches.forEach((swatch) => {
        if (isDisabled) {
          swatch.classList.add('product-card__swatch--disabled');
          swatch.setAttribute('aria-disabled', 'true');
        } else {
          swatch.classList.remove('product-card__swatch--disabled');
          swatch.removeAttribute('aria-disabled');
        }
      });
    }

    showError() {
      let errorElement = this.querySelector('.product-card-swatches__error');
      if (!errorElement) {
        errorElement = document.createElement('div');
        errorElement.className = 'product-card-swatches__error';
        errorElement.setAttribute('role', 'alert');
        errorElement.setAttribute('aria-live', 'polite');
        errorElement.textContent = theme.locales.product.swatchesError;
        this.appendChild(errorElement);
      }
      errorElement.hidden = false;
    }

    hideError() {
      const errorElement = this.querySelector('.product-card-swatches__error');
      if (errorElement) {
        errorElement.hidden = true;
      }
    }
  }

  customElements.define('product-card-swatches', ProductCardSwatches);
}
